<script setup>
import { computed, ref, shallowRef } from 'vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';

useSessionGuard();

// The disciplinary actions the Prefect of Discipline assigns to a case
// (Student Discipline). An action already used on cases is archived rather
// than deleted, so those cases keep its name; archived actions aren't
// offered on new cases and can be restored.
const headers = [
  { title: 'Disciplinary action', key: 'name' },
  { title: 'Status', key: 'archived', width: 140 },
];
const { items, loading, error, load } = useListResource(() =>
  apiRequest('get', '/parent-records/disciplinary-actions', undefined, { params: { includeArchived: true } }),
);
load();

const tab = shallowRef('active');
const tabs = [
  { id: 'active', name: 'In use' },
  { id: 'archived', name: 'Archived' },
];
const shown = computed(() => items.value.filter((a) => Boolean(a.archived) === (tab.value === 'archived')));

const editing = shallowRef(null); // null = add
const formOpen = shallowRef(false);
const form = ref({ name: '' });
const fields = [
  {
    key: 'name',
    label: 'Action',
    required: true,
    placeholder: 'e.g. Community service',
    rules: [(v) => String(v ?? '').trim().length <= 100 || 'Keep it under 100 characters.'],
  },
];
const { busy, run } = useAsyncAction();

function openForm(action = null) {
  editing.value = action;
  form.value = { name: action?.name ?? '' };
  formOpen.value = true;
}

async function save() {
  const data = { name: form.value.name.trim() };
  const ok = await run(
    () =>
      editing.value
        ? apiRequest('patch', `/parent-records/disciplinary-actions/${editing.value.id}`, data)
        : apiRequest('post', '/parent-records/disciplinary-actions', data),
    { success: editing.value ? 'Action updated.' : 'Action added.', error: 'Could not save the action.' },
  );
  if (ok) {
    formOpen.value = false;
    load();
  }
}

async function setArchived(action, archived) {
  const ok = await run(
    () => apiRequest('patch', `/parent-records/disciplinary-actions/${action.id}`, { archived }),
    { success: archived ? 'Action archived.' : 'Action restored.', error: 'Could not update the action.' },
  );
  if (ok) load();
}

const rowActions = (action) => [
  { title: 'Edit', icon: 'mdi-pencil-outline', onClick: () => openForm(action) },
  action.archived
    ? { title: 'Restore', icon: 'mdi-archive-arrow-up-outline', onClick: () => setArchived(action, false) }
    : { title: 'Archive', icon: 'mdi-archive-outline', destructive: true, onClick: () => setArchived(action, true) },
];
</script>

<template>
  <UiTableCard
    v-model:tab="tab"
    title="Disciplinary Actions"
    description="The actions you can assign to a discipline case. Archive an action to stop offering it; cases that already have it keep it."
    :headers="headers"
    :items="shown"
    :tabs="tabs"
    :loading="loading"
    :error="error"
    search-placeholder="Search actions"
    add-label="Add action"
    :empty-text="tab === 'archived' ? 'No archived actions.' : 'No disciplinary actions yet.'"
    row-label="name"
    :row-actions="rowActions"
    @add="openForm()"
  >
    <template #[`item.name`]="{ item }">
      <span class="font-weight-medium">{{ item.name }}</span>
    </template>
    <template #[`item.archived`]="{ item }">
      <UiBadge :variant="item.archived ? 'secondary' : 'green'">{{ item.archived ? 'Archived' : 'In use' }}</UiBadge>
    </template>
  </UiTableCard>

  <UiFormDialog
    v-model="formOpen"
    :title="editing ? 'Edit disciplinary action' : 'Add disciplinary action'"
    description="Renaming an action also renames it on the cases that have it."
    v-model:values="form"
    :fields="fields"
    :saving="busy"
    :submit-text="editing ? 'Save changes' : 'Add action'"
    @submit="save"
  />
</template>
